import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { UserPlus, Camera, CheckCircle2, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { resizeImageFile, imageErrorToast } from "@/lib/imageUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Standalone axios instance — public form must NOT send any Authorization header
// (might be cached from a previous login on the same device) since the endpoint
// is intentionally unauthenticated.
const publicApi = axios.create({ baseURL: API_BASE, timeout: 45_000 });

const initialForm = {
  name: "",
  phone: "",
  email: "",
  gender: "",
  age: "",
  date_of_birth: "",
  address: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  photo_data: "",
};

export default function PublicJoinPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [linkError, setLinkError] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photoProcessing, setPhotoProcessing] = useState(false);
  // Feb 2026 — Feature 7: disclaimer + mandatory acknowledgement modal.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    publicApi
      .get(`/public/join/${token}`)
      .then((r) => {
        if (!cancelled) {
          setInfo(r.data);
          const color = r.data?.studio_branding?.primary_color;
          if (color) {
            document.documentElement.style.setProperty("--brand-primary", color);
          }
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setLinkError(e.response?.data?.detail || "This registration link is invalid or has expired.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingInfo(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const dobDerivedAge = useMemo(() => {
    if (!form.date_of_birth) return null;
    const dob = new Date(form.date_of_birth);
    if (isNaN(dob.getTime())) return null;
    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) years -= 1;
    return years >= 0 && years <= 120 ? years : null;
  }, [form.date_of_birth]);

  useEffect(() => {
    if (dobDerivedAge != null && String(form.age) !== String(dobDerivedAge)) {
      setForm((prev) => ({ ...prev, age: dobDerivedAge }));
    }
  }, [dobDerivedAge, form.age]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    // Always reset the input value so the same file can be re-selected after an error.
    if (e.target) e.target.value = "";
    if (!file) return;

    // Pre-flight: be generous here since we will resize anyway. 15 MB covers any
    // raw camera photo from a modern phone. The real safety net is the resize step.
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Photo is too large (max 15 MB). Try taking a fresh photo instead of using an old gallery image.");
      return;
    }

    setPhotoProcessing(true);
    try {
      const { dataUrl, sizeBytes } = await resizeImageFile(file);
      setForm((prev) => ({ ...prev, photo_data: dataUrl }));
      // Friendly progress toast — confirms success and rough size.
      const kb = Math.round(sizeBytes / 1024);
      toast.success(`Photo ready (${kb} KB)`);
    } catch (err) {
      toast.error(imageErrorToast(err));
    } finally {
      setPhotoProcessing(false);
    }
  };

  const ageLocked = dobDerivedAge != null;
  const phoneValid = /^[6-9]\d{9}$/.test(form.phone);
  const canSubmit =
    form.name.trim().length > 0 &&
    phoneValid &&
    Number(form.age) >= 1 &&
    Number(form.age) <= 99 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await publicApi.post(`/public/join/${token}`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        gender: form.gender || undefined,
        age: Number(form.age),
        date_of_birth: form.date_of_birth || undefined,
        address: form.address.trim() || undefined,
        emergency_contact_name: form.emergency_contact_name.trim() || undefined,
        emergency_contact_phone: form.emergency_contact_phone.trim() || undefined,
        photo_data: form.photo_data || undefined,
      });
      setSubmitted(true);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (e.code === "ECONNABORTED" || e.message?.includes("timeout")) {
        toast.error("The connection timed out. Check your network and try again.");
      } else if (e.code === "ERR_NETWORK") {
        toast.error("Couldn't reach the server. Check your network and try again.");
      } else if (status === 413) {
        toast.error("Your photo is too large. Please remove it or take a smaller one.");
      } else if (status === 429) {
        toast.error("Too many submissions from this network. Please try again later.");
      } else if (status === 409) {
        toast.error("A student with this mobile number is already registered.");
      } else if (status === 400 && detail) {
        toast.error(detail);
      } else {
        toast.error(detail || "Couldn't submit. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-violet-50">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }
  if (linkError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-violet-50">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
          <p className="text-lg font-semibold text-slate-900 mb-2">Link unavailable</p>
          <p className="text-sm text-slate-600">{linkError}</p>
        </div>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-emerald-50 to-violet-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center"
        >
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">You're in! 🎉</h2>
          <p className="text-sm text-slate-600">
            Thanks for registering with <strong>{info?.studio_name || "us"}</strong>.
            The studio will reach out to you shortly to set up classes.
          </p>
        </motion.div>
      </div>
    );
  }

  const logoUrl = info?.studio_branding?.logo_url;
  const orgName = info?.organization_name || "DanceHub";
  const studioName = info?.studio_name || "Our Studio";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50 py-6 px-4">
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl overflow-hidden"
        >
          {/* Branded header */}
          <div
            className="p-6 text-center text-white"
            style={{ background: "linear-gradient(135deg, var(--brand-primary, #7C3AED) 0%, var(--brand-primary-end, #5b21b6) 100%)" }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className="w-16 h-16 mx-auto mb-3 rounded-xl bg-white/20 object-contain p-1" />
            ) : (
              <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-90" />
            )}
            <h1 className="text-2xl font-bold" data-testid="public-join-title">Join {studioName}</h1>
            <p className="text-white/85 text-sm mt-1">Fill in your details to register</p>
          </div>

          <div className="p-6 space-y-4">
            {/* Feb 2026 — Feature 7: disclaimer at the top, above the form. */}
            <div
              data-testid="join-disclaimer"
              className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900"
            >
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <p className="text-xs leading-relaxed">
                <span className="font-semibold">Important:</span> Form submission does not confirm admission or reserve a seat. Admission is confirmed only after fee payment and subject to batch availability.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Student Name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="public-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="h-12 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label>Mobile Number <span className="text-red-500">*</span></Label>
              <Input
                data-testid="public-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                placeholder="10-digit Indian mobile"
                inputMode="numeric"
                className="h-12 rounded-xl"
              />
              {form.phone && !phoneValid && (
                <p className="text-xs text-red-600">Enter a valid 10-digit mobile starting with 6, 7, 8 or 9</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input
                  data-testid="public-dob"
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Age <span className="text-red-500">*</span></Label>
                <select
                  data-testid="public-age"
                  value={form.age || ""}
                  onChange={(e) => setForm({ ...form, age: e.target.value ? Number(e.target.value) : "" })}
                  disabled={ageLocked}
                  className="w-full h-12 rounded-xl border px-3 bg-white disabled:bg-slate-50 disabled:text-slate-500"
                >
                  <option value="">{ageLocked ? `${dobDerivedAge}` : "Select"}</option>
                  {Array.from({ length: 99 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Gender</Label>
                <select
                  data-testid="public-gender"
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full h-12 rounded-xl border px-3 bg-white"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  data-testid="public-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Optional"
                  className="h-12 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                data-testid="public-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Optional"
                className="h-12 rounded-xl"
              />
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium text-slate-700">Emergency Contact</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    data-testid="public-emergency-name"
                    value={form.emergency_contact_name}
                    onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
                    placeholder="Parent name"
                    className="h-12 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    data-testid="public-emergency-phone"
                    value={form.emergency_contact_phone}
                    onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                    inputMode="numeric"
                    placeholder="10-digit"
                    className="h-12 rounded-xl"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 space-y-2">
              <Label>Student Photo</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
                data-testid="public-photo-input"
              />
              <div className="flex items-center gap-3">
                {photoProcessing ? (
                  <div
                    data-testid="public-photo-loading"
                    className="w-16 h-16 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center"
                  >
                    <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
                  </div>
                ) : form.photo_data ? (
                  <img
                    data-testid="public-photo-thumb"
                    src={form.photo_data}
                    alt="Student"
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Camera className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  data-testid="public-photo-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoProcessing}
                  className="rounded-xl"
                >
                  {photoProcessing
                    ? "Processing…"
                    : form.photo_data
                    ? "Change Photo"
                    : "Add Photo"}
                </Button>
                {form.photo_data && !photoProcessing && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setForm({ ...form, photo_data: "" })}
                    className="rounded-xl text-red-600"
                  >
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-slate-500">
                We'll automatically resize your photo so it uploads quickly even on slow networks.
              </p>
            </div>

            <Button
              data-testid="public-submit"
              onClick={() => {
                // Feb 2026 — Feature 7: open acknowledgement modal instead of
                // submitting directly. Acknowledgement state is reset every
                // time the modal opens so an earlier check doesn't carry over.
                setAcknowledged(false);
                setConfirmOpen(true);
              }}
              disabled={!canSubmit}
              className="w-full h-12 rounded-full text-white"
              style={{ background: "linear-gradient(90deg, var(--brand-primary, #7C3AED) 0%, var(--brand-primary-end, #5b21b6) 100%)" }}
            >
              {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <UserPlus className="w-5 h-5 mr-2" />}
              {submitting ? "Submitting…" : "Register"}
            </Button>
            <p className="text-center text-[10px] text-slate-400">Powered by {orgName}</p>
          </div>
        </motion.div>
      </div>

      {/* Feb 2026 — Feature 7: confirm-with-acknowledgement modal. */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!submitting) setConfirmOpen(o); }}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg" data-testid="confirm-title">Please Confirm</DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2 leading-relaxed">
              <span className="font-semibold text-amber-700">Important:</span> Form submission does not confirm admission or reserve a seat. Admission is confirmed only after fee payment and subject to batch availability.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 mt-3 cursor-pointer select-none">
            <Checkbox
              data-testid="confirm-ack-checkbox"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(!!v)}
              className="mt-0.5"
            />
            <span className="text-sm text-slate-700 leading-relaxed">
              I understand that admission is confirmed only after payment and subject to availability.
            </span>
          </label>
          <div className="flex gap-2 mt-4">
            <Button
              data-testid="confirm-cancel"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              className="flex-1 h-11 rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-submit"
              disabled={!acknowledged || submitting}
              onClick={async () => {
                await handleSubmit();
                setConfirmOpen(false);
              }}
              className="flex-1 h-11 rounded-full text-white disabled:opacity-50"
              style={{ background: "linear-gradient(90deg, var(--brand-primary, #7C3AED) 0%, var(--brand-primary-end, #5b21b6) 100%)" }}
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
